import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'examUserPreferences';
const CATALOG_FILTERS_KEY = 'catalogFilters';
const DEFAULT_FONT_SIZE_PX = 16;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 28;

/** Chars per line at 15px (base) */
const BASE_CHARS_PER_LINE = 85;
const BASE_FONT_SIZE = 15;

export type ThemePreference = 'light' | 'dark';
export type AppMode = 'estudante' | 'professor' | 'empresarial';

export interface CatalogFilters {
  group?: string;
  subGroup1?: string;
  subGroup2?: string;
}

export interface UserPreferences {
  answerFontSizePx: number;
  theme: ThemePreference;
  appMode: AppMode;
}

function getSystemTheme(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const VALID_APP_MODES: AppMode[] = ['estudante', 'professor', 'empresarial'];

function parseAppMode(value: unknown): AppMode {
  if (typeof value === 'string' && VALID_APP_MODES.includes(value as AppMode)) {
    // Migrate empresarial to professor (merged modules)
    if (value === 'empresarial') return 'professor';
    return value as AppMode;
  }
  return 'estudante';
}

function loadPreferences(): UserPreferences {
  try {
    if (typeof window === 'undefined') {
      return { answerFontSizePx: DEFAULT_FONT_SIZE_PX, theme: 'light', appMode: 'estudante' };
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const size = Number(parsed?.answerFontSizePx);
      let theme: ThemePreference = getSystemTheme();
      if (parsed?.theme === 'light' || parsed?.theme === 'dark') {
        theme = parsed.theme;
      } else if (parsed?.theme === 'system') {
        theme = getSystemTheme();
      }
      return {
        answerFontSizePx:
          size >= MIN_FONT_SIZE && size <= MAX_FONT_SIZE ? size : DEFAULT_FONT_SIZE_PX,
        theme,
        appMode: parseAppMode(parsed?.appMode),
      };
    }
    return { answerFontSizePx: DEFAULT_FONT_SIZE_PX, theme: getSystemTheme(), appMode: 'estudante' };
  } catch (_) {}
  return { answerFontSizePx: DEFAULT_FONT_SIZE_PX, theme: getSystemTheme(), appMode: 'estudante' };
}

/** Salva preferências fazendo merge com o que já existe no localStorage,
 * preservando campos extras (ex: fontSizeConfigured) usados por ExamView. */
function savePreferences(prefs: UserPreferences) {
  try {
    if (typeof window !== 'undefined') {
      const existing = (() => {
        try {
          const s = localStorage.getItem(STORAGE_KEY);
          return s ? JSON.parse(s) : {};
        } catch {
          return {};
        }
      })();
      const merged = { ...existing, ...prefs };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    }
  } catch (_) {}
}

/** Chars per line scales inversely with font size */
export function getCharsPerLine(fontSizePx: number): number {
  return Math.round(BASE_CHARS_PER_LINE * (BASE_FONT_SIZE / fontSizePx));
}

export function loadCatalogFilters(): CatalogFilters {
  try {
    if (typeof window === 'undefined') return {};
    const stored = localStorage.getItem(CATALOG_FILTERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') {
        return {
          group: typeof parsed.group === 'string' ? parsed.group : undefined,
          subGroup1: typeof parsed.subGroup1 === 'string' ? parsed.subGroup1 : undefined,
          subGroup2: typeof parsed.subGroup2 === 'string' ? parsed.subGroup2 : undefined,
        };
      }
    }
  } catch (_) {}
  return {};
}

export function saveCatalogFilters(filters: CatalogFilters): void {
  try {
    if (typeof window !== 'undefined') {
      const toSave = {};
      if (filters.group) (toSave as Record<string, string>).group = filters.group;
      if (filters.subGroup1) (toSave as Record<string, string>).subGroup1 = filters.subGroup1;
      if (filters.subGroup2) (toSave as Record<string, string>).subGroup2 = filters.subGroup2;
      localStorage.setItem(CATALOG_FILTERS_KEY, JSON.stringify(toSave));
    }
  } catch (_) {}
}

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  setAnswerFontSize: (px: number) => void;
  setTheme: (theme: ThemePreference) => void;
  setAppMode: (mode: AppMode) => void;
  savePreferences: () => void;
  getCharsPerLine: () => number;
  MIN_FONT_SIZE: number;
  MAX_FONT_SIZE: number;
  DEFAULT_FONT_SIZE_PX: number;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(() => loadPreferences());

  const setAnswerFontSize = useCallback((px: number) => {
    const clamped = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, px));
    setPreferences((p) => ({ ...p, answerFontSizePx: clamped }));
  }, []);

  const setTheme = useCallback((theme: ThemePreference) => {
    setPreferences((p) => {
      const next = { ...p, theme };
      savePreferences(next);
      return next;
    });
  }, []);

  const setAppMode = useCallback((appMode: AppMode) => {
    setPreferences((p) => {
      const next = { ...p, appMode };
      savePreferences(next);
      return next;
    });
  }, []);

  const persist = useCallback(() => {
    savePreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    document.documentElement.dataset.theme = preferences.theme;
  }, [preferences.theme]);

  const charsPerLine = getCharsPerLine(preferences.answerFontSizePx);

  const value: UserPreferencesContextValue = {
    preferences,
    setAnswerFontSize,
    setTheme,
    setAppMode,
    savePreferences: persist,
    getCharsPerLine: () => charsPerLine,
    MIN_FONT_SIZE,
    MAX_FONT_SIZE,
    DEFAULT_FONT_SIZE_PX,
  };

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferencesContextValue {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    return {
      preferences: { answerFontSizePx: DEFAULT_FONT_SIZE_PX, theme: 'light', appMode: 'estudante' as AppMode },
      setAnswerFontSize: () => {},
      setTheme: () => {},
      setAppMode: () => {},
      savePreferences: () => {},
      getCharsPerLine: () => BASE_CHARS_PER_LINE,
      MIN_FONT_SIZE,
      MAX_FONT_SIZE,
      DEFAULT_FONT_SIZE_PX,
    };
  }
  return ctx;
}
